import { useCallback } from "react";
import { format } from "date-fns";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";

import {
	deleteTQC1060_R01,
	downloadCertificatePdf_TQC1060_R01,
	downloadExcel_TQC1060_R01,
	getTQC1060_R01AllCount,
	getTQC1060_R01DetailList,
	getTQC1060_R01List,
	postTQC1060_R01,
	TQC1060_R01Params,
} from "./Api_TQC1060_R01";
import {
	TQC1060_R01_COMP_CD,
	TQC1060_R01HeaderDto,
} from "./Dto_TQC1060_R01";
import {
	TQC1060_R01Filter,
	useTQC1060_R01Store,
} from "./Store_TQC1060_R01";

const toYmd = (date?: Date) => (date ? format(date, "yyyy-MM-dd") : undefined);

const getParams = (filter: TQC1060_R01Filter): TQC1060_R01Params => ({
	compCd: filter.compCd || undefined,
	startDate: toYmd(filter.dateRange?.from),
	endDate: toYmd(filter.dateRange?.to),
	inoutClass: filter.inoutClass || undefined,
	customCd: filter.customCd || undefined,
	itemCd: filter.itemCd || undefined,
});

const getNoDetailMessage = (row: TQC1060_R01HeaderDto) => {
	if (!row.itemNm) {
		return `품목 [${row.itemCd}] 이(가) 품목마스터에 등록되어 있지 않습니다.`;
	}
	if (!row.itemGroup) {
		return `품목 [${row.itemNm}] 에 품목그룹이 지정되지 않았습니다.`;
	}
	return `품목그룹 [${row.itemGroup}] 에 등록된 수입검사(IQC) 검사항목이 없습니다.`;
};

export const Content_TQC1060_R01 = () => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const {
		TQC1060_R01Filter,
		setTQC1060_R01PastFilter,
		TQC1060_R01Info,
		setTQC1060_R01Info,
		setTQC1060_R01List,
		setAllCount,
		selectedTQC1060_R01,
		setSelectedTQC1060_R01,
		initSelectedTQC1060_R01,
		TQC1060_R01DetailList,
		setTQC1060_R01DetailList,
		setOriginalTQC1060_R01DetailList,
		setScrollTop,
		setDetailScrollTop,
		initData,
	} = useTQC1060_R01Store();

	const fetchData = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			setScrollTop(null);
			initSelectedTQC1060_R01();
			setTQC1060_R01DetailList([]);
			setOriginalTQC1060_R01DetailList([]);
			const params = getParams(TQC1060_R01Filter);
			const response = await getTQC1060_R01List("search", params);
			const list = response.data.data ?? [];
			setTQC1060_R01List(list);
			const countResponse = await getTQC1060_R01AllCount(params);
			setAllCount(countResponse.data.data ?? list.length);
			setTQC1060_R01PastFilter(TQC1060_R01Filter);
			list.length > 0
				? showSuccess(`조회 완료 (${list.length}건)`)
				: showWarning("조회된 입고내역이 없습니다.");
			return useTQC1060_R01Store.getState().TQC1060_R01List;
		} catch {
			showError("조회 중 오류가 발생했습니다.");
			return undefined;
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC1060_R01Filter,
		initSelectedTQC1060_R01,
		setAllCount,
		setLoadingSpinner,
		setOriginalTQC1060_R01DetailList,
		setScrollTop,
		setTQC1060_R01DetailList,
		setTQC1060_R01List,
		setTQC1060_R01PastFilter,
	]);

	const handleHeaderRowClick = useCallback(
		async (row: TQC1060_R01HeaderDto) => {
			setDetailScrollTop(null);
			setSelectedTQC1060_R01(row);
			setTQC1060_R01Info({
				inspectorEmp: row.inspectorEmp ?? "",
				inspectorEmpNm: row.inspectorEmpNm ?? "",
				approvorEmpno: row.approvorEmpno ?? "",
				approvorEmpNm: row.approvorEmpNm ?? "",
			});
			try {
				setLoadingSpinner(true);
				const response = await getTQC1060_R01DetailList({
					itemCd: row.itemCd,
					inspectNo: row.inspectNo || undefined,
				});
				if (response.status >= 400 || response.data?.successFlag !== true) {
					setTQC1060_R01DetailList([]);
					setOriginalTQC1060_R01DetailList([]);
					return;
				}
				const detailList = response.data.data ?? [];
				setTQC1060_R01DetailList(detailList);
				setOriginalTQC1060_R01DetailList(
					detailList.map((detail) => ({ ...detail })),
				);
				if (detailList.length === 0) {
					showWarning(getNoDetailMessage(row), 3000);
				}
			} catch {
				showError("검사항목을 조회하지 못했습니다.");
			} finally {
				setLoadingSpinner(false);
			}
		},
		[
			setDetailScrollTop,
			setLoadingSpinner,
			setOriginalTQC1060_R01DetailList,
			setSelectedTQC1060_R01,
			setTQC1060_R01DetailList,
			setTQC1060_R01Info,
		],
	);

	const handleSaveClick = useCallback(async () => {
		if (!selectedTQC1060_R01) {
			showWarning("저장할 입고건을 선택해주세요.");
			return;
		}
		if (TQC1060_R01DetailList.length === 0) {
			showWarning(getNoDetailMessage(selectedTQC1060_R01), 3000);
			return;
		}
		if (!TQC1060_R01Info.inspectorEmp) {
			showWarning("검사자를 입력해주세요.");
			return;
		}
		const header: TQC1060_R01HeaderDto = {
			...selectedTQC1060_R01,
			compCd: selectedTQC1060_R01.compCd || TQC1060_R01_COMP_CD,
			inspectDt:
				selectedTQC1060_R01.inspectDt || format(new Date(), "yyyy-MM-dd"),
			...TQC1060_R01Info,
		};
		try {
			setLoadingSpinner(true);
			const response = await postTQC1060_R01({
				header,
				detailList: TQC1060_R01DetailList,
			});
			if (response.status >= 400 || response.data?.successFlag !== true) return;
			const refreshedList = await fetchData();
			const refreshedRow = refreshedList?.find(
				(row) =>
					row.transportNo === header.transportNo && row.itemNo === header.itemNo,
			);
			if (refreshedRow) {
				await handleHeaderRowClick(refreshedRow);
			}
			showSuccess("저장이 완료되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC1060_R01DetailList,
		TQC1060_R01Info,
		fetchData,
		handleHeaderRowClick,
		selectedTQC1060_R01,
		setLoadingSpinner,
	]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedTQC1060_R01?.inspectNo) {
			showWarning("삭제할 검사결과를 선택해주세요.");
			return;
		}
		try {
			setLoadingSpinner(true);
			const response = await deleteTQC1060_R01([selectedTQC1060_R01]);
			if (response.status >= 400 || response.data?.successFlag !== true) return;
			await fetchData();
			showSuccess("삭제가 완료되었습니다.");
		} catch {
			showError("삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [fetchData, selectedTQC1060_R01, setLoadingSpinner]);

	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC1060_R01(
				getParams(TQC1060_R01Filter),
			);
			if (!response.data.data) {
				showWarning("엑셀로 내보낼 데이터가 없습니다.");
				return;
			}
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "수입검사결과등록.xlsx",
			});
		} catch {
			showError("엑셀 다운로드 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC1060_R01Filter, setLoadingSpinner]);

	const handlePrintClick = useCallback(async () => {
		if (!selectedTQC1060_R01?.transportNo || !selectedTQC1060_R01.itemNo) {
			showWarning("출력할 수입검사 대상을 선택해주세요.");
			return;
		}
		try {
			setLoadingSpinner(true);
			const response = await downloadCertificatePdf_TQC1060_R01({
				transportNo: selectedTQC1060_R01.transportNo,
				itemNo: selectedTQC1060_R01.itemNo,
			});
			const contentType = String(response.headers?.["content-type"] ?? "");
			if (response.status >= 400 || !contentType.includes("application/pdf")) {
				const detail = response.data instanceof Blob
					? (await response.data.text()).trim()
					: "";
				showError(detail || "수입검사 검사성적서 출력 중 오류가 발생했습니다.");
				return;
			}
			const fileUrl = URL.createObjectURL(
				new Blob([response.data], { type: "application/pdf" }),
			);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch (error) {
			const data = (error as { response?: { data?: unknown } })?.response?.data;
			const detail = data instanceof Blob ? (await data.text()).trim() : "";
			showError(detail || "수입검사 검사성적서 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedTQC1060_R01, setLoadingSpinner]);

	const initContainerData = useCallback(() => initData(), [initData]);
	return {
		fetchData,
		handleHeaderRowClick,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		initContainerData,
	};
};
